import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleFindJobVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "find-a-job-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || { articles: [] };
  const ref_data = articles.find((art) => art.slug === ref_id) || {};

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1-tech": "Technology",
    "section-2-healthcare": "Healthcare",
    "section-3-hospitality": "Hospitality",
    "section-4-finance": "Finance",
    "section-5-groups": "Groups",
    networking: "Networking & Job Fairs",
    "local-regulations": "Local Regulations",
    costs: "Costs of Job Search Services",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // Re-labeled the references to point out links or textual references mentioned in the conversation
  const referencesData = [
    {
      url: "https://www.workbc.ca/Jobs-Careers/Find-Jobs/Jobs.aspx",
      label: "WorkBC – Official BC Job Portal",
    },
    {
      url: "https://www.jobbank.gc.ca",
      label: "Government of Canada Job Bank",
    },
    {
      url: "https://www.go2hr.ca/job-board",
      label: "go2HR – Tourism & Hospitality Jobs",
    },
    {
      url: "https://issbc.org/our-services/employment-training",
      label: "ISSofBC – Employment Services for Immigrants",
    },
    {
      text: "Vancouver Convention Centre, 1055 Canada Pl, Vancouver, BC.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // List relevant addresses from the article with approximate coords
  const locations = [
    {
      id: "vancouver-convention-centre",
      lat: 49.28920,
      lng: -123.11380,
      popupText: "Vancouver Convention Centre",
    },
    {
      id: "vancouver-public-library",
      lat: 49.27980,
      lng: -123.11570,
      popupText: "Vancouver Public Library – Central Branch",
    },
    {
      id: "sfu-harbour-centre",
      lat: 49.28460,
      lng: -123.11230,
      popupText: "SFU Harbour Centre",
    },
    {
      id: "ubc-robson-square",
      lat: 49.27540,
      lng: -123.12040,
      popupText: "UBC Robson Square",
    },
    {
      id: "iss-welcome-centre",
      lat: 49.26110,
      lng: -123.06570,
      popupText: "ISSofBC Welcome Centre (2610 Victoria Dr.)",
    },
    {
      id: "launch-academy",
      lat: 49.28220,
      lng: -123.10800,
      popupText: "Launch Academy / Gastown Tech Hub",
    },
  ];

  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>How to Find a Job in Vancouver</title>
        <meta
          name="description"
          content="A comprehensive guide to finding a job in Vancouver, including key industries, networking events, local regulations, and more."
        />
        <meta
          name="keywords"
          content="Vancouver job search, work in Vancouver, newcomers guide, job hunting tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/find-a-job-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="How to Find a Job in Vancouver"
        />
        <meta
          property="og:description"
          content="A thorough guide for newcomers, students, and professionals seeking employment in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/find-a-job-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/find-a-job-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="How to Find a Job in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Actionable job search tips for Vancouver, covering local resources, networking events, and industry insights."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/find-a-job-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "/assets/images/find-a-job-in-vancouver.jpg"}
              alt={ref_data?.["image-desc"] || "Finding a job in Vancouver"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is known for its diverse economy, high quality of life,
              and scenic West Coast views. The city has become a magnet for
              job seekers worldwide—offering opportunities in technology,
              healthcare, finance, hospitality, and more. This guide provides
              actionable steps to navigate Vancouver’s job market, whether
              you’re a newcomer, recent graduate, or seasoned professional.
            </p>

            {/* SECTION: Technology */}
            <h2 id="section-1-tech">Technology</h2>
            <p>
              Vancouver’s tech sector is growing rapidly, earning it
              the moniker “Silicon Valley North.” Major players like Amazon,
              Microsoft, and SAP have offices here, alongside a thriving startup
              scene. In-demand roles include software developers, data
              scientists, UX designers, and AI specialists. Networking is crucial,
              with many roles filled by referral and active participation in local
              tech meetups.
            </p>
            <p>
              Notable job boards include BCtechjobs.ca, T-Net, and VanHack.
              Skills in cloud platforms, programming languages (Python,
              JavaScript), and Agile methodologies are particularly valued.
            </p>

            {/* SECTION: Healthcare */}
            <h2 id="section-2-healthcare">Healthcare</h2>
            <p>
              The healthcare sector in Vancouver spans public health authorities
              like Vancouver Coastal Health and Providence Health Care, with
              consistent demand for nurses, healthcare assistants, and allied
              health professionals. Credential recognition is often the largest
              hurdle for newcomers, so be sure to research the necessary
              licensing requirements. Additionally, programs like Health Match BC
              can help match your qualifications to local vacancies.
            </p>

            {/* SECTION: Hospitality */}
            <h2 id="section-3-hospitality">Hospitality</h2>
            <p>
              Tourism is a significant driver of Vancouver’s economy, creating
              many roles in hotels, restaurants, and event management. High
              seasons—like summer and December holidays—see bulk hiring for
              front-desk agents, servers, cooks, tour guides, and more. Sites
              like go2HR specialize in hospitality and tourism job postings.
            </p>

            {/* SECTION: Finance */}
            <h2 id="section-4-finance">Finance</h2>
            <p>
              Vancouver is the financial hub of Western Canada, with roles in
              banking, investment, real estate finance, and insurance. Major
              Canadian banks (RBC, TD, BMO, etc.) have local offices, and
              global firms often recruit from the city’s talent pool. In-demand
              credentials include CPA for accounting and CFA for investment
              roles, while languages like Mandarin or Cantonese can be beneficial
              in serving Vancouver’s diverse communities.
            </p>

            {/* SECTION: Different Groups */}
            <h2 id="section-5-groups">Job Search for Different Groups</h2>
            <p>
              <strong>Newcomers & Immigrants:</strong> Ensure you have the
              correct work permit, and utilize services like MOSAIC, ISSofBC, and
              SUCCESS for credential recognition and job placement programs. 
              Networking remains essential—seek out local newcomer events and
              immigrant-focused job fairs.
            </p>
            <p>
              <strong>Students & Fresh Graduates:</strong> Leverage your
              campus career centers (UBC, SFU, BCIT) and co-op programs, which
              often lead directly to job offers. Attending on-campus recruitment
              sessions and building connections with alumni can speed up your
              job search.
            </p>
            <p>
              <strong>Experienced Professionals:</strong> Many senior roles are
              in the “hidden job market,” so headhunters and executive search
              firms can be valuable. Focus on quantifying your achievements in
              resumes, and keep your LinkedIn profile active and robust.
            </p>

            {/* SECTION: Networking & Job Fairs */}
            <h2 id="networking">Networking & Job Fairs</h2>
            <p>
              Networking is often the differentiator in Vancouver’s relatively
              tight-knit job market. Below are key locations hosting
              meetups, industry events, and job fairs:
            </p>
            <ul>
              <li
                ref={(el) => setSpotRef("vancouver-convention-centre", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver Convention Centre</strong>{" "}
                <Marker
                  coordY={49.28920}
                  coordX={-123.11380}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Hosts large-scale career fairs and industry conventions.
                Many sector-specific or newcomer-focused fairs happen here.
              </li>
              <li
                ref={(el) => setSpotRef("vancouver-public-library", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver Public Library – Central Branch</strong>{" "}
                <Marker
                  coordY={49.27980}
                  coordX={-123.11570}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Regularly holds free career workshops, resume clinics, and
                networking sessions in its lower levels.
              </li>
              <li
                ref={(el) => setSpotRef("sfu-harbour-centre", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>SFU Harbour Centre</strong>{" "}
                <Marker
                  coordY={49.28460}
                  coordX={-123.11230}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – A downtown campus venue for professional panels and
                meetups, easily accessed via Waterfront Station.
              </li>
              <li
                ref={(el) => setSpotRef("ubc-robson-square", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>UBC Robson Square</strong>{" "}
                <Marker
                  coordY={49.27540}
                  coordX={-123.12040}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Hosts career fairs, info sessions, and industry competitions;
                also used by UBC Continuing Studies.
              </li>
              <li
                ref={(el) => setSpotRef("iss-welcome-centre", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>ISSofBC Welcome Centre (2610 Victoria Dr.)</strong>{" "}
                <Marker
                  coordY={49.26110}
                  coordX={-123.06570}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Key spot for newcomer networking sessions, employer panels,
                and hiring events targeting immigrants.
              </li>
              <li
                ref={(el) => setSpotRef("launch-academy", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Launch Academy / Gastown Tech Hub</strong>{" "}
                <Marker
                  coordY={49.28220}
                  coordX={-123.10800}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />{" "}
                – Startup incubator frequently hosting tech community events
                like “Launch Talks” and developer meetups.
              </li>
            </ul>
            <p>
              Staying active in meetups, LinkedIn groups, and sector-specific
              gatherings is essential. Always follow up promptly with new
              contacts. A concise “elevator pitch” about your background and
              goals can help people remember you, and consistent event
              attendance can steadily grow your professional network.
            </p>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Job seekers in Vancouver must comply with BC’s employment laws.
              Minimum wage is currently $17.40/hr (as of June 1, 2024). Employment
              standards set rules on overtime, breaks, and vacation pay. Many
              roles are unionized, especially in healthcare and public sector.
            </p>
            <p>
              If you’re an immigrant, verify your eligibility to work (work
              permit, permanent residency, etc.). Local labor laws protect you
              from discrimination based on age, gender, or ethnicity. Harassment
              is prohibited, and you are entitled to safe working conditions under
              WorkSafeBC.
            </p>

            {/* SECTION: Costs of Job Search Services */}
            <h2 id="costs">Costs of Job Search Services</h2>
            <p>
              While basic job search resources (like WorkBC) are free,
              professional services may enhance your prospects:
            </p>
            <ul>
              <li>
                <strong>Resume Writing:</strong> Usually ranges from
                CAD $100 to $700 or more, depending on the complexity and
                seniority level.
              </li>
              <li>
                <strong>Career Coaching:</strong> Typically CAD $100–$250/hour.
                Some coaches offer package deals for multiple sessions.
              </li>
              <li>
                <strong>Professional Development:</strong> Workshops and short
                courses can cost from a few hundred to a few thousand dollars,
                especially if pursuing a formal certificate program.
              </li>
            </ul>
            <p>
              Always consider free options first, such as WorkBC centers,
              non-profit agencies, or campus career services. However, paid
              services can be a worthwhile investment to speed up your job
              search, especially at mid to senior levels.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Can I apply for jobs in Vancouver from outside Canada?</h3>
              <p>
                Yes, you can. Many employers conduct remote interviews and hire
                international applicants, especially for high-demand fields
                like tech. Ensure you specify your work permit status or whether
                you need sponsorship. Being local can help with networking, but
                it’s not mandatory.
              </p>

              <h3>2. What are the best job sites for Vancouver?</h3>
              <p>
                WorkBC and the Government of Canada Job Bank are top official
                portals. Indeed and LinkedIn are widely used by both big and
                small employers. For industry-focused searches, try BC Tech Jobs
                (tech), go2HR (hospitality), and Health Match BC (healthcare).
              </p>

              <h3>3. How important is networking for finding a job in Vancouver?</h3>
              <p>
                Extremely important. Vancouver is a relatively small big city,
                and many positions are filled through referrals or “hidden job
                market” openings. Regularly attending meetups, job fairs, and
                professional gatherings can significantly increase your odds
                of finding opportunities.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s job market offers something for everyone, from
              cutting-edge tech roles to in-demand healthcare positions to
              vibrant hospitality and finance sectors. Success involves
              customizing your approach: use the resources and tips in this
              guide, stay proactive in networking, and remember your rights
              under BC’s labor laws. With persistence and preparation, you
              can find fulfilling employment while enjoying all the West
              Coast lifestyle has to offer.
            </p>
            <p>
              <em>Disclaimer: Always check official resources or consult local
              authorities for the latest regulations and visa requirements.
              This guide is not legal advice.</em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}